import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import ProviderAuthLayout from "../components/auth/ProviderAuthLayout";

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const otpRefs = useRef([]);

  const validateEmail = (value) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(value).toLowerCase());
  };

  const validatePassword = (value) => {
    const re =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return re.test(value);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setErrorMessage("Invalid email format.");
      return;
    }
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/forgot-password`,
        { email }
      );

      if (response.data.success) {
        setStep(2);
      } else {
        setErrorMessage("Email is not registered.");
      }
    } catch (error) {
      if (!navigator.onLine) {
        setErrorMessage(
          "Network connection lost. Please check your connection and try again."
        );
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "An error occurred. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (element, index) => {
    const value = element.value.replace(/\D/g, "").slice(0, 1);
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < otpRefs.current.length - 1) {
      otpRefs.current[index + 1].focus();
    }

    if (!value && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pastedData) return;
    const newOtp = pastedData.split("").slice(0, otp.length);
    while (newOtp.length < otp.length) newOtp.push("");
    setOtp(newOtp);
    const focusIndex = Math.min(newOtp.filter(Boolean).length, otp.length - 1);
    otpRefs.current[focusIndex]?.focus();
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (otp.includes("")) {
      setErrorMessage("Please fill all OTP fields.");
      return;
    }
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/verify-otp`,
        { email, otp: otp.join("") }
      );
      if (response.data.success) {
        setStep(3);
      } else {
        setErrorMessage("OTP is incorrect. Please try again.");
      }
    } catch (error) {
      if (!navigator.onLine) {
        setErrorMessage(
          "Network connection lost. Please check your connection and try again."
        );
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "An error occurred. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/forgot-password`,
        { email }
      );
      if (response.data.success) {
        setSuccessMessage("A new verification code was sent to your email.");
        setOtp(Array(6).fill(""));
        otpRefs.current[0]?.focus();
      } else {
        setErrorMessage("Unable to resend code. Please try again.");
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          "Unable to resend code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword(password)) {
      setErrorMessage("Password does not meet the required criteria.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/reset-password`,
        { email, password }
      );
      if (response.data.success) {
        setSuccessMessage("Password reset successfully.");
        setTimeout(() => {
          window.location.href = "/login";
        }, 1000);
      } else {
        setErrorMessage("Failed to reset password. Please try again.");
      }
    } catch (error) {
      if (!navigator.onLine) {
        setErrorMessage(
          "Network connection lost. Please check your connection and try again."
        );
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "An error occurred. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProviderAuthLayout>
      {step === 1 && (
        <>
          <h1 className="provider-auth-title">Reset your password</h1>
          <p className="provider-auth-subtitle">
            Enter your email and we&apos;ll send you a reset link
          </p>

          <form className="provider-auth-form" onSubmit={handleEmailSubmit}>
            <div className="provider-auth-field">
              <label className="provider-auth-label" htmlFor="reset-email">
                Email
              </label>
              <input
                id="reset-email"
                className="provider-auth-input form-control"
                type="email"
                placeholder="name@organization.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            {errorMessage ? (
              <p className="provider-auth-error" role="alert">
                {errorMessage}
              </p>
            ) : null}

            <button className="provider-auth-btn" type="submit" disabled={loading}>
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>

          <p className="provider-auth-footer">
            <Link className="provider-auth-link" to="/login">
              Back to Sign In
            </Link>
          </p>
        </>
      )}

      {step === 2 && (
        <>
          <h1 className="provider-auth-title">Enter verification code</h1>
          <p className="provider-auth-subtitle">
            We sent a 6-digit security code to your registered email address.
          </p>

          <form className="provider-auth-form" onSubmit={handleOtpSubmit}>
            <div className="provider-auth-field">
              <label className="provider-auth-label">Security Code</label>
              <div className="provider-auth-otp" onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    className="provider-auth-otp-box form-control"
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    placeholder="-"
                    onChange={(e) => handleOtpChange(e.target, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    ref={(el) => {
                      otpRefs.current[index] = el;
                    }}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {errorMessage ? (
              <p className="provider-auth-error" role="alert">
                {errorMessage}
              </p>
            ) : null}
            {successMessage ? (
              <p className="provider-auth-success">{successMessage}</p>
            ) : null}

            <button className="provider-auth-btn" type="submit" disabled={loading}>
              {loading ? "Verifying..." : "Verify Code"}
            </button>
          </form>

          <p className="provider-auth-footer">
            Didn&apos;t receive the code?{" "}
            <button
              type="button"
              className="provider-auth-link"
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={handleResendCode}
              disabled={loading}
            >
              Resend Code
            </button>
          </p>
          <p className="provider-auth-footer" style={{ marginTop: 10 }}>
            <Link className="provider-auth-link" to="/login">
              Back to Sign In
            </Link>
          </p>
        </>
      )}

      {step === 3 && (
        <>
          <h1 className="provider-auth-title">Create new password</h1>
          <p className="provider-auth-subtitle">
            Your identity has been verified. Please choose a secure password
            that you haven&apos;t used before.
          </p>

          <form className="provider-auth-form" onSubmit={handlePasswordSubmit}>
            <div className="provider-auth-field">
              <label className="provider-auth-label" htmlFor="new-password">
                New Password
              </label>
              <div className="provider-auth-password-wrap">
                <input
                  id="new-password"
                  className="provider-auth-input form-control"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="provider-auth-eye"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </button>
              </div>
            </div>

            <div className="provider-auth-field">
              <label className="provider-auth-label" htmlFor="confirm-password">
                Confirm Password
              </label>
              <div className="provider-auth-password-wrap">
                <input
                  id="confirm-password"
                  className="provider-auth-input form-control"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="provider-auth-eye"
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash size={16} />
                  ) : (
                    <FaEye size={16} />
                  )}
                </button>
              </div>
            </div>

            <p className="provider-auth-hint">
              Must be at least 8 characters long with one uppercase letter, one
              lowercase letter, a number, and a symbol.
            </p>

            {errorMessage ? (
              <p className="provider-auth-error" role="alert">
                {errorMessage}
              </p>
            ) : null}
            {successMessage ? (
              <p className="provider-auth-success">{successMessage}</p>
            ) : null}

            <button className="provider-auth-btn" type="submit" disabled={loading}>
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          <p className="provider-auth-footer">
            <Link className="provider-auth-link" to="/login">
              Back to Sign In
            </Link>
          </p>
        </>
      )}
    </ProviderAuthLayout>
  );
};

export default ForgotPassword;
